// src/admin/components/ReturnViewDialog.tsx
import React, { useState } from 'react';
import {
  Drawer,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  Stack,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  TextField,
  Alert,
  ImageList,
  ImageListItem,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warehouse as WarehouseIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import {
  PurchaseOrderReturn,
  ReturnStatus,
  RETURN_STATUS_COLORS,
  RETURN_REASON_LABELS,
} from './PurchaseOrder';
import { PurchaseOrderReturnService } from './purchaseOrderReturnService';
import { useCompany } from '../../../contexts/CompanyContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { useAuth } from '../../../contexts/AuthContext';

interface ReturnViewDialogProps {
  open: boolean;
  onClose: () => void;
  return: PurchaseOrderReturn;
  onStatusUpdate: (returnId: string, status: ReturnStatus) => void;
  onRefresh: () => void;
}

// ── Comma-separated amount formatter ─────────────────────────────────────────
// Formats a number to 2 decimal places with thousands separators.
// e.g. 1234567.5 → "1,234,567.50"
const formatAmount = (value: number): string =>
  value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Theme-aware chip style helper ─────────────────────────────────────────────
//
// In light mode: soft pastel tints (low-opacity fills).
// In dark mode:  richer semi-transparent fills + brighter text so the chip
//                is legible against dark paper backgrounds.
//
// Pass `isDark` (from useTheme) to switch between the two palettes.
// ─────────────────────────────────────────────────────────────────────────────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

// Map RETURN_STATUS_COLORS values to soft palette keys
const returnStatusToSoftColor: Record<string, string> = {
  success: 'success',
  warning: 'warning',
  info: 'info',
  error: 'error',
  primary: 'primary',
  secondary: 'secondary',
  default: 'default',
};
// ─────────────────────────────────────────────────────────────────────────────

const ReturnViewDialog: React.FC<ReturnViewDialogProps> = ({
  open, onClose, return: returnDoc, onStatusUpdate, onRefresh,
}) => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth();
  const { companyProfile } = useCompany();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // isDark drives all getSoftChipSx calls so chips are legible in both modes
  const isDark = theme.palette.mode === 'dark';

  const [refundAmount, setRefundAmount] = useState('');
  const [completingLoading, setCompletingLoading] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [completionError, setCompletionError] = useState('');

  // ── Status change confirmation dialog state ──────────────────────────────
  const [statusConfirmTarget, setStatusConfirmTarget] = useState<ReturnStatus | null>(null);
  const [statusConfirmLoading, setStatusConfirmLoading] = useState(false);
  // ────────────────────────────────────────────────────────────────────────

  // ── Complete return confirmation dialog state ────────────────────────────
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [completeConfirmLoading, setCompleteConfirmLoading] = useState(false);
  // ────────────────────────────────────────────────────────────────────────

  const canApprove = isSuperAdmin || hasPermission('inventory_return.approve');
  const canReject = isSuperAdmin || hasPermission('inventory_return.reject');
  const canComplete = isSuperAdmin || hasPermission('inventory_return.complete');

  const companyCurrency = companyProfile?.currency || 'LKR';
  const currencySymbol = getCurrencySymbol(companyCurrency);

  // ── Status change: open confirm dialog ──────────────────────────────────
  const handleStatusChange = (status: ReturnStatus) => {
    setStatusConfirmTarget(status);
  };

  const handleStatusConfirm = async () => {
    if (!statusConfirmTarget) return;
    try {
      setStatusConfirmLoading(true);
      setStatusUpdateLoading(true);
      await onStatusUpdate(returnDoc.id!, statusConfirmTarget);
      setStatusConfirmTarget(null);
      onClose();
    } catch (err: any) {
      setStatusConfirmTarget(null);
      alert('Failed to update status: ' + (err.message || 'Unknown error'));
    } finally {
      setStatusConfirmLoading(false);
      setStatusUpdateLoading(false);
    }
  };

  const handleStatusCancel = () => {
    setStatusConfirmTarget(null);
  };
  // ────────────────────────────────────────────────────────────────────────

  // ── Complete return: validate then open confirm dialog ───────────────────
  const handleCompleteReturnClick = () => {
    const amount = parseFloat(refundAmount);
    if (isNaN(amount) || amount <= 0) {
      setCompletionError('Please enter a valid refund amount');
      return;
    }
    if (amount > returnDoc.totalAmount) {
      setCompletionError('Refund amount cannot exceed total return amount');
      return;
    }
    setCompletionError('');
    setCompleteConfirmOpen(true);
  };

  const handleCompleteConfirm = async () => {
    if (!currentUser) return;
    const amount = parseFloat(refundAmount);
    try {
      setCompleteConfirmLoading(true);
      setCompletingLoading(true);
      setCompletionError('');
      await PurchaseOrderReturnService.completeReturnWithInventoryAdjustment(
        returnDoc.id!, amount, currentUser.uid,
        currentUser.displayName || currentUser.email || 'Unknown'
      );
      setCompleteConfirmOpen(false);
      onRefresh();
      onClose();
    } catch (err: any) {
      setCompleteConfirmOpen(false);
      setCompletionError(err.message || 'Failed to complete return');
    } finally {
      setCompleteConfirmLoading(false);
      setCompletingLoading(false);
    }
  };

  const handleCompleteCancel = () => {
    setCompleteConfirmOpen(false);
  };
  // ────────────────────────────────────────────────────────────────────────

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 680, md: 780 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

          {/* Header */}
          <Box sx={{
            px: 3, py: 2,
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <Box>
              <Typography variant="h6">Return Details</Typography>
              <Typography variant="body2" color="text.secondary">{returnDoc.returnNumber}</Typography>
            </Box>
            <IconButton onClick={onClose} edge="end"><CloseIcon /></IconButton>
          </Box>

          {/* Scrollable Content */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
            <Grid container spacing={2}>

              {/* Status chips */}
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  <Chip
                    label={`Status: ${returnDoc.status}`}
                    size="small"
                    variant="outlined"
                    sx={getSoftChipSx(returnStatusToSoftColor[RETURN_STATUS_COLORS[returnDoc.status]] ?? 'default', isDark)}
                  />
                  {returnDoc.inventoryAdjusted && (
                    <Chip
                      label="Inventory Adjusted"
                      size="small"
                      variant="outlined"
                      icon={<CheckCircleIcon />}
                      sx={getSoftChipSx('success', isDark)}
                    />
                  )}
                  {returnDoc.refundIssued && (
                    <Chip
                      label={`Refund Issued: ${currencySymbol} ${formatAmount(returnDoc.refundAmount)}`}
                      size="small"
                      variant="outlined"
                      icon={<AttachMoneyIcon />}
                      sx={getSoftChipSx('info', isDark)}
                    />
                  )}
                </Stack>
              </Grid>

              {/* PO Reference */}
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Purchase Order</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{returnDoc.purchaseOrderNumber}</Typography>
                </Paper>
              </Grid>

              {/* Supplier */}
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Supplier Information</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>{returnDoc.supplierName}</Typography>
                  <Typography variant="body2" color="text.secondary">{returnDoc.supplierEmail}</Typography>
                </Paper>
              </Grid>

              {/* Return Items Table */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Returned Items</Typography>
                <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Variant</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Warehouse</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Reason</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {returnDoc.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.productName}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                              {item.variantName || item.sku}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                              {RETURN_REASON_LABELS[item.reason]}
                            </Typography>
                            {item.notes && (
                              <Typography variant="caption" color="text.secondary">Note: {item.notes}</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Typography variant="caption" color="text.secondary">{item.variantName || '-'}</Typography>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            {item.warehouseName
                              ? (
                                <Chip
                                  label={item.warehouseName}
                                  size="small"
                                  variant="outlined"
                                  icon={<WarehouseIcon />}
                                  sx={getSoftChipSx('default', isDark)}
                                />
                              )
                              : <Typography variant="caption" color="text.secondary">-</Typography>}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={item.quantity}
                              size="small"
                              variant="outlined"
                              sx={getSoftChipSx('error', isDark)}
                            />
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Chip
                              label={RETURN_REASON_LABELS[item.reason]}
                              size="small"
                              variant="outlined"
                              sx={getSoftChipSx('default', isDark)}
                            />
                          </TableCell>
                          {/* Total — plain text.primary, comma-separated, no hardcoded colour */}
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {currencySymbol} {formatAmount(item.totalPrice)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>

              {/* Return Summary
                  ── bgcolor: action.hover + divider border works in light & dark.
                  ── All Typography uses text.primary / text.secondary only — no
                  hardcoded error/success colour on text per design requirement.
                  ── Amounts formatted with formatAmount() for comma-separated
                  thousands.
              */}
              <Grid item xs={12} sm={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Return Summary</Typography>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Total Return Amount:</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{currencySymbol} {formatAmount(returnDoc.totalAmount)}</Typography>
                    </Stack>
                    {returnDoc.refundIssued && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Refund Issued:</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {currencySymbol} {formatAmount(returnDoc.refundAmount)}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </Grid>

              {/* Complete Return
                  ── bgcolor: action.hover + divider border works in light & dark.
              */}
              {canComplete && returnDoc.status === ReturnStatus.APPROVED && !returnDoc.inventoryAdjusted && (
                <Grid item xs={12} sm={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: 'action.hover',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Complete Return</Typography>
                    {completionError && <Alert severity="error" sx={{ mb: 2 }}>{completionError}</Alert>}
                    <Stack spacing={2}>
                      <TextField
                        size="small" type="number" label="Refund Amount"
                        value={refundAmount}
                        onChange={(e) => setRefundAmount(e.target.value)}
                        inputProps={{ min: 0, max: returnDoc.totalAmount, step: 0.01 }}
                        fullWidth
                      />
                      <Typography variant="caption" color="text.secondary">
                        Maximum: {currencySymbol} {formatAmount(returnDoc.totalAmount)}
                      </Typography>
                      <Button
                        variant="contained" color="success"
                        onClick={handleCompleteReturnClick}
                        disabled={completingLoading || !refundAmount}
                        startIcon={completingLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                        fullWidth
                      >
                        {completingLoading ? 'Processing...' : 'Complete & Adjust Inventory'}
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>
              )}

              {/* Return Reason */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Return Reason</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{returnDoc.reason}</Typography>
                </Paper>
              </Grid>

              {/* Notes */}
              {returnDoc.notes && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Notes</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{returnDoc.notes}</Typography>
                  </Paper>
                </Grid>
              )}
              {returnDoc.internalNotes && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'warning.lighter' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Internal Notes</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{returnDoc.internalNotes}</Typography>
                  </Paper>
                </Grid>
              )}

              {/* Images */}
              {returnDoc.images && returnDoc.images.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Supporting Images</Typography>
                  <ImageList cols={isMobile ? 2 : 4} gap={8}>
                    {returnDoc.images.map((image, index) => (
                      <ImageListItem key={index}>
                        <img
                          src={image}
                          alt={`Return evidence ${index + 1}`}
                          loading="lazy"
                          style={{ cursor: 'pointer', borderRadius: 4 }}
                          onClick={() => window.open(image, '_blank')}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Grid>
              )}

              {/* Timeline
                  ── bgcolor: action.hover + divider border works in light & dark.
                  ── was bgcolor: 'grey.50' which disappears in dark mode.
              */}
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Timeline</Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      Created: {returnDoc.createdAt.toLocaleDateString()} by {returnDoc.createdByName}
                    </Typography>
                    {returnDoc.approvedByName && (
                      <Typography variant="body2">
                        Approved: {returnDoc.approvedAt?.toLocaleDateString()} by {returnDoc.approvedByName}
                      </Typography>
                    )}
                    {returnDoc.completedByName && (
                      <Typography variant="body2">
                        Completed: {returnDoc.completedAt?.toLocaleDateString()} by {returnDoc.completedByName}
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </Grid>

            </Grid>
          </Box>

          {/* Footer */}
          <Divider />
          <Box sx={{ px: 3, py: 2, flexShrink: 0 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {returnDoc.status === ReturnStatus.PENDING && (
                  <>
                    {canApprove && (
                      <Button
                        onClick={() => handleStatusChange(ReturnStatus.APPROVED)}
                        variant="outlined" color="success"
                        startIcon={<CheckCircleIcon />}
                        disabled={statusUpdateLoading}
                        size="small"
                      >
                        Approve
                      </Button>
                    )}
                    {canReject && (
                      <Button
                        onClick={() => handleStatusChange(ReturnStatus.REJECTED)}
                        variant="outlined" color="error"
                        startIcon={<CancelIcon />}
                        disabled={statusUpdateLoading}
                        size="small"
                      >
                        Reject
                      </Button>
                    )}
                  </>
                )}
              </Stack>
              <Button onClick={onClose} variant="contained" size="small" sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' } }}>
                Close
              </Button>
            </Stack>
          </Box>
        </Box>
      </Drawer>

      {/* ── Status Change Confirmation Dialog ── */}
      <Dialog open={!!statusConfirmTarget} onClose={handleStatusCancel} maxWidth="xs" fullWidth>
        <DialogTitle>
          {statusConfirmTarget === ReturnStatus.APPROVED ? 'Approve Return' : 'Reject Return'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to change the status to{' '}
            <strong>"{statusConfirmTarget}"</strong>?{' '}
            This action will update the return status immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleStatusCancel} disabled={statusConfirmLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={statusConfirmTarget === ReturnStatus.APPROVED ? 'success' : 'error'}
            onClick={handleStatusConfirm}
            disabled={statusConfirmLoading}
            startIcon={
              statusConfirmLoading
                ? <CircularProgress size={16} />
                : statusConfirmTarget === ReturnStatus.APPROVED
                  ? <CheckCircleIcon />
                  : <CancelIcon />
            }
          >
            {statusConfirmLoading
              ? 'Updating...'
              : statusConfirmTarget === ReturnStatus.APPROVED
                ? 'Approve'
                : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Complete Return Confirmation Dialog ── */}
      <Dialog open={completeConfirmOpen} onClose={handleCompleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Complete Return</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Complete this return with a refund of{' '}
            <strong>
              {currencySymbol} {formatAmount(parseFloat(refundAmount || '0'))}
            </strong>?{' '}
            This will adjust inventory and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCompleteCancel} disabled={completeConfirmLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCompleteConfirm}
            disabled={completeConfirmLoading}
            startIcon={completeConfirmLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {completeConfirmLoading ? 'Processing...' : 'Complete & Adjust Inventory'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReturnViewDialog;